import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { AtsMessageBoardConversationFragment } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

import createComputed from 'modules/core/hooks/createComputed'
import { diffBetweenDateTimes } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { isConversationRead, isConversationUnread } from 'modules/ats/lib/utils/conversation'

type ConversationFilters = {
  search: string
  status: string
  watcher: string
}

const useSearchFilter = createComputed(
  (search: string, conversations: AtsMessageBoardConversationFragment[]) => {
    if (isEmpty(search)) {
      return conversations
    }

    return conversations.filter(
      (conversation: AtsMessageBoardConversationFragment) =>
        matchText(search, conversation.application.candidate.fullName) ||
        matchText(search, conversation.application.jobPosting.title)
    )
  }
)

const useOrderConversations = createComputed(
  (conversations: AtsMessageBoardConversationFragment[]) => {
    return conversations.sort(
      (
        conversationA: AtsMessageBoardConversationFragment,
        conversationB: AtsMessageBoardConversationFragment
      ) => {
        const isReadA = isConversationRead(conversationA)
        const isReadB = isConversationRead(conversationB)

        if (isReadA === isReadB) {
          return diffBetweenDateTimes(
            conversationB.lastMessage?.createdAt,
            conversationA.lastMessage?.createdAt
          )
        }

        if (isReadA) return 1
        return -1
      }
    )
  }
)

const useConversationStatusFilter = createComputed(
  (status: ConversationFilters['status'], conversations: AtsMessageBoardConversationFragment[]) => {
    if (status === 'archived') {
      return conversations.filter((conversation) => !!conversation.conversationStatus?.archivedAt)
    }

    const activeConversations = conversations.filter(
      (conversation) => !conversation.conversationStatus?.archivedAt
    )

    if (status === 'unread_by_me') {
      return activeConversations.filter(isConversationUnread)
    }

    if (status === 'unread_by_candidate') {
      return activeConversations.filter(
        (conversation) =>
          conversation.lastMessage?.sentByType === 'User' && !conversation.lastMessage?.openedAt
      )
    }

    if (status === 'unreplied_by_candidate') {
      return activeConversations.filter(
        (conversation) => conversation.lastMessage?.sentByType === 'User'
      )
    }

    return activeConversations
  }
)

const DEFAULT_FILTERS: ConversationFilters = {
  search: '',
  status: 'all',
  watcher: 'watcher',
}

export const useConversationsFilter = () => {
  const [storedFilters, setFilters] = useLocalStorage<ConversationFilters>(
    LOCAL_STORAGE_KEYS.atsMessagesConversationsFilters,
    DEFAULT_FILTERS
  )

  const filters = storedFilters || DEFAULT_FILTERS

  const onFiltersChange = (newFilters: Partial<ConversationFilters>) => {
    setFilters({
      ...filters,
      ...newFilters,
    })
  }

  const serverFilters = {
    watcher: filters.watcher == 'watcher',
    archived: filters.status == 'archived',
  }

  const options = {
    watcher: [
      {
        label: i18n.t('ats.messages.watcher.yes'),
        value: 'watcher',
      },
      {
        label: i18n.t('ats.messages.watcher.no'),
        value: 'not_watcher',
      },
    ],
    status: [
      {
        label: i18n.t('ats.messages.status.all'),
        value: 'all',
      },
      {
        label: i18n.t('ats.messages.status.unread_by_me'),
        value: 'unread_by_me',
      },
      {
        label: i18n.t('ats.messages.status.unread_by_candidate'),
        value: 'unread_by_candidate',
      },
      {
        label: i18n.t('ats.messages.status.unreplied_by_candidate'),
        value: 'unreplied_by_candidate',
      },
      {
        label: i18n.t('ats.messages.status.archived'),
        value: 'archived',
      },
    ],
  }

  return {
    filters,
    serverFilters,
    onFiltersChange,
    options,
  }
}

export const useFilteredConversations = (
  conversations: AtsMessageBoardConversationFragment[],
  filters: ConversationFilters
) => {
  const openConversations = conversations.filter((conversation) => !!conversation.lastMessage)
  const filterBySearch = useSearchFilter(filters.search, openConversations).get()
  const filterByConversationStatus = useConversationStatusFilter(
    filters.status,
    filterBySearch
  ).get()

  const orderedConversations = useOrderConversations(filterByConversationStatus).get()
  const totalConversationsCount = orderedConversations.length

  return {
    filteredConversations: orderedConversations,
    totalConversationsCount,
  }
}
